import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Wrench, Save, RotateCcw, Plus, Trash2, Upload, FileCheck2, AlertCircle, SlidersHorizontal } from 'lucide-react';
import SizingRulesPage from './SizingRulesPage';
import { overridePvProductConfig } from '../../config/pv-product-config';
import { overrideGeneralProductCatalog } from '../../config/general-product-config';

export default function ConfiguratorAdminPage() {
  const { user } = useAuth();
  const { can, ready: permReady } = usePermissions();
  const [systems, setSystems] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const fileRef = useRef(null);
  // 04.02.2026: Matrix-basiertes Gating statt `user.role === 'admin'`.
  // Wer `configurator.admin` in der Aktionsmatrix hat, darf die Seite
  // benutzen — die Rolle ist egal.
  const isAdmin = can('configurator.admin');
  const [mode, setMode] = useState('systems');   // 'systems' | 'rules'

  const load = useCallback(async () => {
    setLoading(true);
    const res = await authFetch('/api/configurator/pv-systems');
    if (res.ok) {
      const d = await res.json();
      // `originalModel` merken — damit wir beim Speichern wissen, ob das
      // Modell umbenannt wurde (Rename via separatem Endpoint vs. normales
      // Update).
      const list = (d.systems || []).map(s => ({ ...s, originalModel: s.model, originalFamily: s.family }));
      setSystems(list);
      if (!activeModel && list.length) setActiveModel(list[0].model);
      // 04.02.2026 — Live-Sync: nach jedem Admin-Save/Load auch die globale
      // `pvProductConfig` (die vom PV-Konfigurator+FragHans genutzt wird)
      // mit den frischen MPPT-Daten überschreiben. Bisher wurde diese
      // nur beim App-Mount geladen, sodass ein Admin-Rename der Tracker-
      // IDs (z. B. „1" → „T1") oder eine geänderte `maxPvKw` erst nach
      // vollständigem Page-Reload wirkte.
      try {
        overridePvProductConfig(d.systems || [], d.evalOrder);
      } catch (_) { /* silent — nicht kritisch für die Admin-Seite */ }
      // Ebenso die zentrale System-Registry (general-systems) aktualisieren,
      // damit Frag HANS und der Kunden-System-Konfigurator sofort das neue
      // System kennen — ohne Wartezeit auf einen Cache-Refresh.
      try {
        const genRes = await authFetch('/api/configurator/general-systems');
        if (genRes.ok) {
          const gd = await genRes.json();
          overrideGeneralProductCatalog(gd.systems || []);
        }
      } catch (_) { /* silent */ }
    }
    setLoading(false);
  }, [activeModel]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Aktives System via `originalModel` finden — wenn der User das Modell
  // im Form-Feld ändert, soll der Tab weiterhin auf den gleichen Eintrag
  // zeigen.
  const currentSystem = systems.find(s => (s.originalModel || s.model) === activeModel);

  const updateSystem = (patch) => {
    setSystems(prev => prev.map(s => (s.originalModel || s.model) === activeModel ? { ...s, ...patch } : s));
  };

  const updateMppt = (index, field, value) => {
    const mppts = [...(currentSystem?.mppts || [])];
    mppts[index] = { ...mppts[index], [field]: field === 'id' ? value : Number(value) };
    updateSystem({ mppts });
  };

  const addMppt = () => {
    const mppts = [...(currentSystem?.mppts || [])];
    const nextId = `T${mppts.length + 1}`;
    mppts.push({ id: nextId, minV: 65, maxV: 450, maxA: 16, maxPvKw: 3.6 });
    updateSystem({ mppts });
  };

  const removeMppt = (index) => {
    const mppts = (currentSystem?.mppts || []).filter((_, i) => i !== index);
    updateSystem({ mppts });
  };

  const save = async () => {
    if (!currentSystem) return;
    setSaving(true); setMessage(null);

    const origModel = currentSystem.originalModel || activeModel;
    const origFamily = currentSystem.originalFamily || currentSystem.family;
    const modelRenamed = currentSystem.model !== origModel;
    const familyRenamed = currentSystem.family !== origFamily;

    // 1) Falls Modell oder Familie umbenannt: erst Rename-Endpoint
    //    aufrufen (verschiebt auch verknüpfte Knowledge-Base-Einträge).
    if (modelRenamed || familyRenamed) {
      const confirmMsg = modelRenamed
        ? `Modell von „${origModel}" → „${currentSystem.model}" umbenennen?\n\n` +
          `Verknüpfte Knowledge-Base-Einträge werden automatisch mit aktualisiert. ` +
          `Diese Aktion kann nicht rückgängig gemacht werden.`
        : `Familie von „${origFamily}" → „${currentSystem.family}" umbenennen?\n\n` +
          `Verknüpfte Knowledge-Base-Einträge werden automatisch mit aktualisiert.`;
      if (!window.confirm(confirmMsg)) { setSaving(false); return; }
      const renameRes = await authFetch(
        `/api/configurator/pv-systems/${encodeURIComponent(origModel)}/rename`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newModel: currentSystem.model, newFamily: currentSystem.family }) },
      );
      if (!renameRes.ok) {
        setSaving(false);
        const d = await renameRes.json().catch(() => ({}));
        setMessage({ type: 'error', text: d.detail || 'Umbenennen fehlgeschlagen' });
        return;
      }
      const renameResult = await renameRes.json();
      const extraInfo = [];
      if (renameResult.knowledgeModelCodesUpdated) extraInfo.push(`${renameResult.knowledgeModelCodesUpdated} Wissens-Einträge (Modell-Codes)`);
      if (renameResult.knowledgeFamiliesUpdated)   extraInfo.push(`${renameResult.knowledgeFamiliesUpdated} Wissens-Einträge (Familie)`);
      const extra = extraInfo.length ? ` + ${extraInfo.join(' + ')} mitaktualisiert` : '';
      setMessage({ type: 'success', text: `Umbenannt zu „${currentSystem.model}"${extra}.` });
    }

    // 2) Normales PUT auf den (ggf. neuen) Modell-Namen.
    const targetModel = currentSystem.model;
    const res = await authFetch(`/api/configurator/pv-systems/${encodeURIComponent(targetModel)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(currentSystem),
    });
    setSaving(false);
    if (res.ok) {
      // Tab-Active auf neuen Namen umstellen, sodass nach load() der
      // gleiche Eintrag wieder offen ist.
      if (modelRenamed) setActiveModel(targetModel);
      if (!modelRenamed && !familyRenamed) {
        setMessage({ type: 'success', text: `${targetModel} gespeichert — ab sofort wirksam im Konfigurator und bei Frag HANS.` });
      }
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMessage({ type: 'error', text: d.detail || 'Speichern fehlgeschlagen' });
    }
  };

  const reset = async () => {
    if (!window.confirm('Alle Systeme auf Werkseinstellungen zurücksetzen? Aktuelle Änderungen gehen verloren.')) return;
    const res = await authFetch('/api/configurator/pv-systems/reset', { method: 'POST' });
    if (res.ok) { setMessage({ type: 'success', text: 'Werkseinstellungen wiederhergestellt.' }); load(); }
  };

  // ── Datasheet upload & extraction ─────────────────────────────────────
  const API = API_BASE;
  const token = localStorage.getItem('ees_token');

  const handleDatasheetUpload = async (file) => {
    if (!file) return;
    if (/\.xlsx?$/i.test(file.name)) {
      return handleExcelUpload(file);
    }
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Nur PDF- oder Excel-Datenblätter werden unterstützt.' });
      return;
    }
    setUploading(true); setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/api/configurator/pv-systems/extract-datasheet`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: d.detail || 'Extraktion fehlgeschlagen' });
        return;
      }
      const data = await res.json();
      setExtraction(data.extracted);
      setMessage({ type: 'success', text: `Datenblatt "${data.fileName}" analysiert. Felder prüfen und übernehmen.` });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExcelUpload = async (file) => {
    setUploading(true); setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/api/configurator/pv-systems/extract-excel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: d.detail || 'Excel-Parse fehlgeschlagen' });
        return;
      }
      const data = await res.json();
      const drafts = data.systems || [];
      if (drafts.length === 0) {
        setMessage({ type: 'error', text: 'Keine Systeme in der Excel-Datei erkannt.' });
        return;
      }
      if (!window.confirm(`${drafts.length} Systeme aus "${data.fileName}" (${data.sheets.length} Blätter) erkannt. Alle importieren/aktualisieren?`)) return;
      // Upsert each: if model exists → PUT, else → POST
      const existing = new Set(systems.map(s => s.model));
      for (const sys of drafts) {
        const url = existing.has(sys.model)
          ? `/api/configurator/pv-systems/${encodeURIComponent(sys.model)}`
          : '/api/configurator/pv-systems';
        const method = existing.has(sys.model) ? 'PUT' : 'POST';
        try {
          await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sys) });
        } catch (_) { /* skip silently */ }
      }
      setMessage({ type: 'success', text: `Excel importiert — ${drafts.length} Systeme aktualisiert.` });
      load();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const mergeExtraction = () => {
    if (!extraction || !currentSystem) return;
    const merge = { ...currentSystem };
    const numFields = [
      'recommendedPvKw', 'maxFeedInKw', 'standardUsableKwh', 'maxDischargeKw',
      'backupContinuousKva', 'backupPeakKva30s', 'efficiencyPercent', 'weightKg',
      'acousticDb', 'warrantyYears',
    ];
    numFields.forEach(f => {
      if (extraction[f] != null && !Number.isNaN(Number(extraction[f]))) merge[f] = Number(extraction[f]);
    });
    ['protectionClass'].forEach(f => { if (extraction[f]) merge[f] = extraction[f]; });
    if (extraction.dimensions) merge.dimensions = { ...(merge.dimensions || {}), ...extraction.dimensions };
    if (extraction.operatingTempRange) merge.operatingTempRange = { ...(merge.operatingTempRange || {}), ...extraction.operatingTempRange };
    if (Array.isArray(extraction.mppts) && extraction.mppts.length) merge.mppts = extraction.mppts;
    if (Array.isArray(extraction.certifications)) merge.certifications = extraction.certifications;
    if (Array.isArray(extraction.features)) merge.features = extraction.features;
    if (Array.isArray(extraction.customFields)) {
      const existing = merge.customFields || [];
      merge.customFields = [...existing, ...extraction.customFields];
    }
    updateSystem(merge);
    setExtraction(null);
    setMessage({ type: 'success', text: 'Werte aus Datenblatt übernommen — bitte prüfen und speichern.' });
  };

  if (!isAdmin) {
    if (!permReady) return <div className="p-6 text-sm text-gray-400">Lade…</div>;
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">Konfigurator-Verwaltung ist nur mit dem Recht „PV-Systeme verwalten" verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl" data-testid="configurator-admin">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#9ec043]" /> Konfigurator-Verwaltung
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {mode === 'systems'
              ? 'MPPT-Spezifikationen und PV-Systemdaten. Änderungen wirken sofort im PV-Konfigurator und bei Frag HANS.'
              : 'Auslegungs-Regelwerk: Basisfaktoren, Uplifts, Min/Max-Grenzen für Frag HANS, Konfigurator und Anfragen-Auswertung.'}
          </p>
        </div>
        <div className="flex gap-2">
          {mode === 'systems' && (
            <>
              <Button variant="outline" onClick={() => {
                const model = window.prompt('Modellname des neuen Systems (z. B. HS 20):');
                if (!model || !model.trim()) return;
                const body = {
                  family: model.split(' ')[0] || model, model: model.trim(),
                  recommendedPvKw: 0, maxFeedInKw: 0, standardUsableKwh: 0,
                  maxDischargeKw: 0, backupContinuousKva: 0, backupPeakKva30s: 0,
                  mppts: [], groupLimits: [], preferredPrimaryTrackers: [],
                };
                authFetch('/api/configurator/pv-systems', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
                }).then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.detail); }))
                  .then(() => { setActiveModel(model.trim()); load(); setMessage({ type: 'success', text: `System "${model.trim()}" angelegt.` }); })
                  .catch(e => setMessage({ type: 'error', text: e.message || 'Fehler' }));
              }} className="h-9 text-sm" data-testid="btn-new-system">
                <Plus className="w-3.5 h-3.5 mr-1" /> System hinzufügen
              </Button>
              <Button variant="outline" onClick={reset} className="h-9 text-sm" data-testid="btn-reset-cfg">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Werkseinstellungen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mode-Toggle: Systeme | Regelwerk */}
      <div className="flex gap-2 mb-4" data-testid="cfg-mode-toggle">
        <button
          onClick={() => setMode('systems')}
          className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 ${
            mode === 'systems'
              ? 'bg-[#9ec043] text-white border-[#9ec043]'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="cfg-mode-systems">
          <Wrench className="w-4 h-4" /> Systeme
        </button>
        <button
          onClick={() => setMode('rules')}
          className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 ${
            mode === 'rules'
              ? 'bg-[#9ec043] text-white border-[#9ec043]'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="cfg-mode-rules">
          <SlidersHorizontal className="w-4 h-4" /> Regelwerk
        </button>
      </div>

      {mode === 'rules' ? (
        <SizingRulesPage embedded />
      ) : (
      <>
      {message && (
        <div className={`rounded p-3 mb-4 text-sm border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`} data-testid="cfg-message">
          {message.text}
        </div>
      )}

      {/* System tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-4">
        {systems.map(s => {
          const tabKey = s.originalModel || s.model;
          const dirty = (s.originalModel && s.model !== s.originalModel) || (s.originalFamily && s.family !== s.originalFamily);
          return (
            <button
              key={tabKey}
              onClick={() => setActiveModel(tabKey)}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${activeModel === tabKey ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid={`cfg-tab-${tabKey.replace(/\s/g, '-')}`}
            >
              {s.model}{dirty && <span className="ml-1 text-amber-500" title="Modell/Familie geändert — zum Speichern unten klicken">●</span>}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-sm text-gray-400">Lade…</div>}

      {!loading && currentSystem && (
        <div className="space-y-6">
          {/* Datasheet upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#9ec043]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Upload className="w-4 h-4 text-[#9ec043]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-[#32373c]">Datenblatt hochladen (PDF oder Excel)</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  <strong>PDF</strong>: Einzelnes System-Datenblatt — Gemini extrahiert Kenndaten in die aktuelle Ansicht zum Prüfen/Speichern.<br/>
                  <strong>Excel (.xlsx)</strong>: Mehrere Tabellenblätter (1 pro Produktfamilie) — alle Systeme werden auf einmal importiert/aktualisiert.
                </p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,application/pdf" className="hidden"
                         onChange={(e) => handleDatasheetUpload(e.target.files?.[0])}
                         data-testid="cfg-datasheet-file" />
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                          variant="outline" className="h-8 text-xs" data-testid="btn-upload-datasheet">
                    {uploading ? 'Analysiere…' : 'PDF oder Excel auswählen'}
                  </Button>
                  {extraction && (
                    <>
                      <span className="text-[11px] text-gray-400 flex items-center gap-1">
                        <FileCheck2 className="w-3 h-3 text-[#9ec043]" /> Extrakt bereit ({Object.keys(extraction).filter(k => extraction[k] != null && (!Array.isArray(extraction[k]) || extraction[k].length)).length} Felder)
                      </span>
                      <Button onClick={mergeExtraction} className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-8 text-xs" data-testid="btn-merge-extraction">
                        In Formular übernehmen
                      </Button>
                      <Button variant="outline" onClick={() => setExtraction(null)} className="h-8 text-xs">Verwerfen</Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Core system parameters */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-[#32373c] mb-4">Systemkenndaten</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Modell" value={currentSystem.model}
                     onChange={v => updateSystem({ model: v })}
                     testid="cfg-model" />
              <Field label="Familie" value={currentSystem.family}
                     onChange={v => updateSystem({ family: v })}
                     testid="cfg-family" />
              <NumField label="Empf. PV-Leistung (kWp)" value={currentSystem.recommendedPvKw}
                        onChange={v => updateSystem({ recommendedPvKw: Number(v) })} testid="cfg-recommendedPvKw" />
              <NumField label="Max. Einspeisung (kW)" value={currentSystem.maxFeedInKw}
                        onChange={v => updateSystem({ maxFeedInKw: Number(v) })} testid="cfg-maxFeedInKw" />
              <NumField label="Nutzkapazität (kWh)" value={currentSystem.standardUsableKwh}
                        onChange={v => updateSystem({ standardUsableKwh: Number(v) })} testid="cfg-standardUsableKwh" />
              <NumField label="Max. Entladeleistung (kW)" value={currentSystem.maxDischargeKw}
                        onChange={v => updateSystem({ maxDischargeKw: Number(v) })} testid="cfg-maxDischargeKw" />
              <NumField label="Notstrom Dauer (kVA)" value={currentSystem.backupContinuousKva}
                        onChange={v => updateSystem({ backupContinuousKva: Number(v) })} testid="cfg-backupContinuousKva" />
              <NumField label="Notstrom Peak 30s (kVA)" value={currentSystem.backupPeakKva30s}
                        onChange={v => updateSystem({ backupPeakKva30s: Number(v) })} testid="cfg-backupPeakKva30s" />
              <NumField label="Wirkungsgrad (%)" value={currentSystem.efficiencyPercent}
                        onChange={v => updateSystem({ efficiencyPercent: v === '' ? null : Number(v) })} testid="cfg-efficiencyPercent" />
            </div>
          </div>

          {/* Mechanical / environment */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-[#32373c] mb-4">Mechanik & Umgebung</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <NumField label="Breite (mm)" value={currentSystem.dimensions?.widthMm}
                        onChange={v => updateSystem({ dimensions: { ...(currentSystem.dimensions || {}), widthMm: v === '' ? null : Number(v) } })} testid="cfg-widthMm" />
              <NumField label="Höhe (mm)" value={currentSystem.dimensions?.heightMm}
                        onChange={v => updateSystem({ dimensions: { ...(currentSystem.dimensions || {}), heightMm: v === '' ? null : Number(v) } })} testid="cfg-heightMm" />
              <NumField label="Tiefe (mm)" value={currentSystem.dimensions?.depthMm}
                        onChange={v => updateSystem({ dimensions: { ...(currentSystem.dimensions || {}), depthMm: v === '' ? null : Number(v) } })} testid="cfg-depthMm" />
              <NumField label="Gewicht (kg)" value={currentSystem.weightKg}
                        onChange={v => updateSystem({ weightKg: v === '' ? null : Number(v) })} testid="cfg-weightKg" />
              <Field label="Schutzart (z. B. IP65)" value={currentSystem.protectionClass || ''}
                     onChange={v => updateSystem({ protectionClass: v })} testid="cfg-protectionClass" />
              <NumField label="Betriebstemperatur Min (°C)" value={currentSystem.operatingTempRange?.minC}
                        onChange={v => updateSystem({ operatingTempRange: { ...(currentSystem.operatingTempRange || {}), minC: v === '' ? null : Number(v) } })} testid="cfg-tempMin" />
              <NumField label="Betriebstemperatur Max (°C)" value={currentSystem.operatingTempRange?.maxC}
                        onChange={v => updateSystem({ operatingTempRange: { ...(currentSystem.operatingTempRange || {}), maxC: v === '' ? null : Number(v) } })} testid="cfg-tempMax" />
              <NumField label="Geräusch (dB)" value={currentSystem.acousticDb}
                        onChange={v => updateSystem({ acousticDb: v === '' ? null : Number(v) })} testid="cfg-acousticDb" />
              <NumField label="Garantie (Jahre)" value={currentSystem.warrantyYears}
                        onChange={v => updateSystem({ warrantyYears: v === '' ? null : Number(v) })} testid="cfg-warrantyYears" />
            </div>
          </div>

          {/* Chips: certifications + features */}
          <ChipListSection
            title="Zertifikate"
            values={currentSystem.certifications || []}
            onChange={(list) => updateSystem({ certifications: list })}
            placeholder="z. B. VDE-AR-N 4105"
            testidBase="cfg-cert"
          />
          <ChipListSection
            title="Funktionen & Features"
            values={currentSystem.features || []}
            onChange={(list) => updateSystem({ features: list })}
            placeholder="z. B. Notstrom, Schwarzstart"
            testidBase="cfg-feature"
          />

          {/* Custom extensible fields */}
          <CustomFieldsSection
            fields={currentSystem.customFields || []}
            onChange={(list) => updateSystem({ customFields: list })}
          />

          {/* MPPT matrix */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#32373c]">MPPT-Tracker</h2>
              <Button variant="outline" size="sm" onClick={addMppt} className="h-8 text-xs" data-testid="btn-add-mppt">
                <Plus className="w-3.5 h-3.5 mr-1" /> Tracker hinzufügen
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-2 font-medium w-20">ID</th>
                    <th className="text-left py-2 font-medium">Min V (Startspannung)</th>
                    <th className="text-left py-2 font-medium">Max V</th>
                    <th className="text-left py-2 font-medium">Max A</th>
                    <th className="text-left py-2 font-medium">Max kWp</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {(currentSystem.mppts || []).map((t, i) => (
                    <tr key={i} className="border-b border-gray-50" data-testid={`mppt-row-${i}`}>
                      <td className="py-2 pr-2">
                        <Input value={t.id} onChange={e => updateMppt(i, 'id', e.target.value)}
                               className="h-8 text-xs w-16" data-testid={`mppt-id-${i}`} />
                      </td>
                      <td className="py-2 pr-2"><Input type="number" step="any" value={t.minV} onChange={e => updateMppt(i, 'minV', e.target.value)} className="h-8 text-xs w-28" data-testid={`mppt-minV-${i}`} /></td>
                      <td className="py-2 pr-2"><Input type="number" step="any" value={t.maxV} onChange={e => updateMppt(i, 'maxV', e.target.value)} className="h-8 text-xs w-28" data-testid={`mppt-maxV-${i}`} /></td>
                      <td className="py-2 pr-2"><Input type="number" step="any" value={t.maxA} onChange={e => updateMppt(i, 'maxA', e.target.value)} className="h-8 text-xs w-24" data-testid={`mppt-maxA-${i}`} /></td>
                      <td className="py-2 pr-2"><Input type="number" step="any" value={t.maxPvKw} onChange={e => updateMppt(i, 'maxPvKw', e.target.value)} className="h-8 text-xs w-24" data-testid={`mppt-maxPvKw-${i}`} /></td>
                      <td className="py-2">
                        <button onClick={() => removeMppt(i)} className="text-gray-300 hover:text-red-500" title="Entfernen" data-testid={`btn-del-mppt-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Min V = Mindest-Startspannung des Trackers. Sizing rechnet intern einen Sicherheitsabschlag von ~18 % gegen Sommer-Vmp.
            </p>
          </div>

          {/* Save row */}
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving}
                    className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-save-cfg">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Speichert…' : 'Änderungen speichern'}
            </Button>
            <Button variant="outline" onClick={async () => {
              if (!window.confirm(`System "${activeModel}" unwiderruflich löschen?`)) return;
              const r = await authFetch(`/api/configurator/pv-systems/${encodeURIComponent(activeModel)}`, { method: 'DELETE' });
              if (r.ok) { setMessage({ type: 'success', text: `"${activeModel}" gelöscht.` }); setActiveModel(null); load(); }
            }} className="h-9 text-sm text-red-600 hover:text-red-700" data-testid="btn-delete-system">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> System löschen
            </Button>
            <span className="text-[11px] text-gray-400">Wirkt sofort im Konfigurator und bei Frag HANS.</span>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function Field({ label, value, disabled, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input value={value ?? ''} disabled={disabled}
             onChange={e => onChange?.(e.target.value)}
             className={`mt-1 h-8 text-sm ${disabled ? 'bg-gray-50' : ''}`}
             data-testid={testid} />
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input type="number" step="any" value={value ?? ''} onChange={e => onChange(e.target.value)}
             className="mt-1 h-8 text-sm" data-testid={testid} />
    </div>
  );
}

function ChipListSection({ title, values, onChange, placeholder, testidBase }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(values || []), v]);
    setDraft('');
  };
  const remove = (idx) => onChange(values.filter((_, i) => i !== idx));
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-[#32373c] mb-3">{title}</h2>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {values.length === 0 && <span className="text-xs text-gray-400">Noch keine Einträge.</span>}
        {values.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-[#9ec043]/10 text-[#9ec043] text-xs px-2 py-1 rounded-full" data-testid={`${testidBase}-${i}`}>
            {v}
            <button onClick={() => remove(i)} className="hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={e => setDraft(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
               placeholder={placeholder} className="h-8 text-sm flex-1"
               data-testid={`${testidBase}-input`} />
        <Button onClick={add} variant="outline" className="h-8 text-xs" data-testid={`${testidBase}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Hinzufügen
        </Button>
      </div>
    </div>
  );
}

function CustomFieldsSection({ fields, onChange }) {
  const update = (i, key, val) => {
    const next = [...fields];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...(fields || []), { label: '', value: '', unit: '', group: 'Sonstiges' }]);
  const remove = (i) => onChange(fields.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-[#32373c]">Weitere Kenndaten (frei erweiterbar)</h2>
          <p className="text-[11px] text-gray-400 mt-0.5">Alles, was nicht in die Standardfelder oben passt — Kommunikation, Sensoren, spezielle Grenzwerte, Anschlussarten …</p>
        </div>
        <Button onClick={add} variant="outline" className="h-8 text-xs" data-testid="btn-add-customfield">
          <Plus className="w-3.5 h-3.5 mr-1" /> Feld hinzufügen
        </Button>
      </div>
      {fields.length === 0 && <p className="text-xs text-gray-400">Noch keine zusätzlichen Felder.</p>}
      {fields.length > 0 && (
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`custom-row-${i}`}>
              <div className="col-span-3">
                <Label className="text-[10px] text-gray-400">Bezeichnung</Label>
                <Input value={f.label || ''} onChange={e => update(i, 'label', e.target.value)}
                       className="h-8 text-xs" data-testid={`custom-label-${i}`} />
              </div>
              <div className="col-span-4">
                <Label className="text-[10px] text-gray-400">Wert</Label>
                <Input value={f.value || ''} onChange={e => update(i, 'value', e.target.value)}
                       className="h-8 text-xs" data-testid={`custom-value-${i}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-gray-400">Einheit</Label>
                <Input value={f.unit || ''} onChange={e => update(i, 'unit', e.target.value)}
                       className="h-8 text-xs" data-testid={`custom-unit-${i}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-gray-400">Gruppe</Label>
                <select value={f.group || 'Sonstiges'} onChange={e => update(i, 'group', e.target.value)}
                        className="w-full h-8 text-xs rounded-md border border-gray-200 bg-white px-2"
                        data-testid={`custom-group-${i}`}>
                  {['Elektrisch', 'Mechanisch', 'Umgebung', 'Schutz', 'Kommunikation', 'Sonstiges'].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <button onClick={() => remove(i)} className="h-8 text-gray-300 hover:text-red-500 flex items-end" data-testid={`btn-del-custom-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
